/**
 * Client Details Page - AI Search Portal
 * Full inline view of client information with buyer/seller profiles
 */

import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Badge, Alert, Spinner, Nav, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUser,
  faHome,
  faDollarSign,
  faRobot,
  faEnvelope,
  faPhone,
  faCalendar,
  faBrain,
  faChartLine,
  faEdit,
  faLink,
  faCopy,
  faCheckCircle,
  faUserPlus,
  faClipboardList,
  faLightbulb,
  faMapMarkerAlt,
  faClock,
  faHandHoldingUsd,
  faBolt,
  faExclamationTriangle,
  faHeart,
  faStar,
  faTrophy,
  faRefresh,
  faFilter,
  faWandMagicSparkles,
  faMagic,
  faComments,
  faFileInvoiceDollar
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useParams } from 'react-router-dom';
import agentPortalService from '../../services/agentPortal.service';
import LeadAiAssistant from '../../components/modules/crm/LeadAiAssistant';
import BuyerProfileModal from '../../components/modals/BuyerProfileModal';
import SellerProfilePage from '../../components/modules/crm/SellerProfilePage';
import SellerProfileModal from '../../components/modals/SellerProfileModal';
import PropertyCard from '../../components/common/PropertyCard';
import AISearchTab from '../../components/modules/crm/AISearchTab';
import SellerAICompAnalysis from '../../components/modules/crm/SellerAICompAnalysis';
import axiosInstance from '../../helpers/api';
import { toast } from 'react-hot-toast';
import './ClientDetailsPage.css';

interface ClientDetails {
  lead: {
    id: string;
    name: string;
    email: string;
    phone: string;
    address?: string;
    created: string;
    leadSource?: string;
    leadType?: string;
  };
  profileType: 'buyer' | 'seller' | 'both' | 'none';
  buyerProfile?: any;
  sellerProfile?: any;
  aiContext: {
    completenessScore: number;
    journeyStage: string;
    lastAnalyzed: string;
    insights: any;
  };
  hasPortalAccess: boolean;
  portalLink?: string;
}

const ClientDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { leadId } = useParams<{ leadId: string }>();
  const [loading, setLoading] = useState(true);
  const [clientDetails, setClientDetails] = useState<ClientDetails | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  // Read initial tab from URL hash or default to overview
  const getTabFromUrl = () => {
    const hash = window.location.hash.replace('#', '');
    if (['overview', 'ai-search', 'buyer', 'seller', 'seller-ai', 'activity'].includes(hash)) return hash;
    return 'overview';
  };
  const [activeTab, setActiveTab] = useState(getTabFromUrl());

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    window.history.pushState(null, '', `${window.location.pathname}#${tab}`);
  };
  const [showBuyerProfileModal, setShowBuyerProfileModal] = useState(false);
  const [showSellerProfileModal, setShowSellerProfileModal] = useState(false);
  
  // AI Property Matching states
  const [aiProperties, setAiProperties] = useState<any[]>([]);
  const [aiPropertiesLoading, setAiPropertiesLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<any>(null);
  
  // Helper function to parse BigDecimal values
  const parseBigDecimal = (value: any): number | null => {
    if (!value) return null;
    if (typeof value === 'number') return value;
    if (value.d && Array.isArray(value.d) && value.d.length > 0) {
      return value.d[0];
    }
    return null;
  };

  useEffect(() => {
    if (leadId) {
      fetchClientDetails();
    }
  }, [leadId]);
  
  // Fetch AI properties when Overview tab is active and buyer profile exists
  useEffect(() => {
    if (activeTab === 'overview' && clientDetails?.buyerProfile && !aiProperties.length && !aiPropertiesLoading) {
      fetchAiPropertyMatches();
    }
  }, [activeTab, clientDetails]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      
      // Get single client profile
      const profileResponse = await agentPortalService.getClientProfile(leadId!);
      
      if (profileResponse.success && profileResponse.data) {
        const profile = profileResponse.data;
        // Get additional search preferences if available
        let searchPrefs = null;
        try {
          searchPrefs = await agentPortalService.getSearchPreferences(leadId!);
        } catch (err) {
          console.log('No search preferences found');
        }

        // Transform the data into our format
        // Transform buyer profile to expected format
        let transformedBuyerProfile = null;
        if (profile.buyer_profile) {
          const bp = profile.buyer_profile;
          transformedBuyerProfile = {
            ...bp,
            // Keep all original fields
            buyer_stage: bp.buyer_stage || bp.stage || 'researching',
            buying_timeframe: bp.buying_timeframe || bp.timeframe || 'not_specified',
            pre_approval_amount: bp.pre_approval_amount,
            first_time_buyer: bp.first_time_buyer || false,
            // Ensure property_preferences is properly structured
            property_preferences: {
              ...bp.property_preferences,
              budget_min: bp.property_preferences?.budget_min || 0,
              budget_max: bp.property_preferences?.budget_max || 0,
            }
          };
        }
        
        // Transform seller profile to expected format
        let transformedSellerProfile = null;
        if (profile.seller_profile) {
          const sp = profile.seller_profile;
          console.log('🔍 Raw seller_profile from API:', sp);
          transformedSellerProfile = {
            ...sp,
            selling_timeframe: sp.selling_timeframe || sp.timeframe || 'not_specified',
            reason_for_selling: sp.reason_for_selling || sp.reason,
            // Map address fields correctly - they are direct properties, not nested
            property_address: sp.property_address || sp.property?.address,
            property_city: sp.property_city || sp.property?.city,
            property_state: sp.property_state || sp.property?.state,
            property_zip: sp.property_zip || sp.property?.zip,
            desired_price: sp.desired_price || parseBigDecimal(sp.property?.asking_price),
            // Use DB values with sensible defaults only when DB value is missing
            motivation_level: sp.motivation_level || 'medium',
            flexibility_level: sp.flexibility_level || 'moderate',
            property_type: sp.property_type || 'single_family',
            occupancy_status: sp.occupancy_status || 'owner_occupied',
            property_condition: sp.property_condition || 'good'
          };
        }
        
        const details: ClientDetails = {
          lead: {
            id: profile.lead.id,
            name: profile.lead.name,
            email: profile.lead.email,
            phone: profile.lead.phone,
            created: profile.lead.created,
            leadSource: profile.lead.source,
            leadType: profile.lead.type
          },
          profileType: profile.profile_type,
          buyerProfile: transformedBuyerProfile,
          sellerProfile: transformedSellerProfile,
          aiContext: {
            completenessScore: profile.ai_context.completeness_score || 0,
            journeyStage: profile.ai_context.journey_stage || 'unknown',
            lastAnalyzed: profile.ai_context.last_analyzed,
            insights: profile.ai_context.buyer_context
          },
          hasPortalAccess: !!profile.lead.has_portal_access,
          portalLink: profile.lead.has_portal_access ? 
            `https://aisearchportal.com/customer/${profile.lead.id}` : undefined
        };

        console.log('💾 Final clientDetails with sellerProfile:', details.sellerProfile);
        setClientDetails(details);
        
        // Set active tab based on profile type
        if (details.profileType === 'buyer') setActiveTab('buyer');
        else if (details.profileType === 'seller') setActiveTab('seller');
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
      toast.error('Failed to load client details');
    } finally {
      setLoading(false);
    }
  };

  const copyPortalLink = () => {
    if (clientDetails?.portalLink) {
      navigator.clipboard.writeText(clientDetails.portalLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      toast.success('Portal link copied!');
    }
  };
  
  // Fetch AI property matches
  const fetchAiPropertyMatches = async () => {
    if (!clientDetails?.buyerProfile || !leadId) return;
    
    try {
      setAiPropertiesLoading(true);
      setAiError(null);
      
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadId}/ai-property-matches`, {
        params: { limit: 10 }
      });
      
      if (response.data.success) {
        setAiProperties(response.data.data.properties);
        setAiSummary(response.data.data.summary);
      } else {
        throw new Error(response.data.message || 'Failed to fetch AI matches');
      }
    } catch (error: any) {
      console.error('Error fetching AI properties:', error);
      setAiError(error.response?.data?.message || 'Failed to load AI property recommendations');
      toast.error('Failed to load AI property recommendations');
    } finally {
      setAiPropertiesLoading(false);
    }
  };

  const enablePortalAccess = async () => {
    try {
      const result = await agentPortalService.enablePortalAccess(leadId!);
      if (result.success) {
        toast.success('Portal access enabled!');
        fetchClientDetails(); // Refresh data
      }
    } catch (error) {
      toast.error('Failed to enable portal access');
    }
  };

  const handleEditProfile = () => {
    if (activeTab === 'buyer') {
      setShowBuyerProfileModal(true);
    } else if (activeTab === 'seller') {
      setShowSellerProfileModal(true);
    } else if (activeTab === 'overview') {
      // If on overview, check which profiles exist
      if (!clientDetails?.buyerProfile && !clientDetails?.sellerProfile) {
        // No profiles exist, default to buyer
        setShowBuyerProfileModal(true);
      } else if (clientDetails?.buyerProfile && !clientDetails?.sellerProfile) {
        // Only buyer exists
        setShowBuyerProfileModal(true);
      } else if (!clientDetails?.buyerProfile && clientDetails?.sellerProfile) {
        // Only seller exists
        setShowSellerProfileModal(true);
      } else {
        // Both exist, default to buyer
        setShowBuyerProfileModal(true);
      }
    }
  };

  const handleProfileSave = () => {
    // Refresh the client details after saving
    fetchClientDetails();
  };

  const getProfileIcon = () => {
    switch (clientDetails?.profileType) {
      case 'buyer':
        return <FontAwesomeIcon icon={faHome} className="text-info" />;
      case 'seller':
        return <FontAwesomeIcon icon={faDollarSign} className="text-warning" />;
      case 'both':
        return <FontAwesomeIcon icon={faHandHoldingUsd} className="text-success" />;
      default:
        return <FontAwesomeIcon icon={faUser} className="text-secondary" />;
    }
  };

  const getProfileBadge = () => {
    const hasBuyer = !!clientDetails?.buyerProfile;
    const hasSeller = !!clientDetails?.sellerProfile;
    
    if (hasBuyer && hasSeller) {
      return <Badge bg="success" className="ms-2">Buyer & Seller</Badge>;
    } else if (hasBuyer) {
      return <Badge bg="info" className="ms-2">Buyer</Badge>;
    } else if (hasSeller) {
      return <Badge bg="warning" text="dark" className="ms-2">Seller</Badge>;
    } else {
      return <Badge bg="secondary" className="ms-2">No Profiles Yet</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!clientDetails) {
    return (
      <Alert variant="danger" className="m-4">
        <FontAwesomeIcon icon={faUser} className="me-2" />
        Client not found or you don't have access to this client.
        <div className="mt-3">
          <Button variant="primary" onClick={() => navigate('/agent-portal/dashboard')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to Dashboard
          </Button>
        </div>
      </Alert>
    );
  }

  return (
    <div className="client-details-page">
      {/* Header */}
      <div className="client-header">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Button 
              variant="link" 
              className="text-decoration-none p-0 me-3"
              onClick={() => navigate('/agent-portal/dashboard')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="d-flex gap-2">
            {clientDetails.hasPortalAccess ? (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={copyPortalLink}
              >
                <FontAwesomeIcon icon={linkCopied ? faCheckCircle : faCopy} className="me-1" />
                {linkCopied ? 'Copied!' : 'Copy Portal Link'}
              </Button>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={enablePortalAccess}
              >
                <FontAwesomeIcon icon={faRobot} className="me-1" />
                Enable Portal Access
              </Button>
            )}
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={handleEditProfile}
            >
              <FontAwesomeIcon icon={faEdit} className="me-1" />
              Edit Profile
            </Button>
          </div>
        </div>

        {/* Client Info Header */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-4">
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center mb-3">
                  <div className="client-avatar-large me-3">
                    {getProfileIcon()}
                  </div>
                  <div>
                    <h2 className="mb-1">
                      {clientDetails.lead.name}
                      {getProfileBadge()}
                    </h2>
                    <div className="text-muted">
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                      {clientDetails.lead.email || 'No email'}
                      <span className="mx-3">•</span>
                      <FontAwesomeIcon icon={faPhone} className="me-2" />
                      {clientDetails.lead.phone || 'No phone'}
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-4 text-muted small">
                  <div>
                    <FontAwesomeIcon icon={faCalendar} className="me-1" />
                    Added {new Date(clientDetails.lead.created).toLocaleDateString()}
                  </div>
                  {clientDetails.lead.leadSource && (
                    <div>
                      <FontAwesomeIcon icon={faUser} className="me-1" />
                      Source: {clientDetails.lead.leadSource}
                    </div>
                  )}
                </div>
              </Col>
              <Col md={4}>
                <Card className="border-0 bg-light">
                  <Card.Body className="text-center">
                    <div className="mb-2">
                      <FontAwesomeIcon icon={faBrain} size="2x" className="text-primary" />
                    </div>
                    <h6 className="mb-1">AI Profile Score</h6>
                    <div className="display-4 fw-bold text-primary">
                      {clientDetails.aiContext.completenessScore}%
                    </div>
                    <Badge bg={clientDetails.aiContext.completenessScore > 80 ? 'success' : 'warning'}>
                      {clientDetails.aiContext.journeyStage}
                    </Badge>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {/* Main Content */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => handleTabChange(k || 'overview')}>
        <Card className="border-0 shadow-sm">
          <Card.Header className="bg-white border-bottom">
            <Nav variant="tabs" className="nav-tabs-modern">
              <Nav.Item>
                <Nav.Link eventKey="overview">
                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                  Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="ai-search">
                  <FontAwesomeIcon icon={faWandMagicSparkles} className="me-2" />
                  AI Search
                  <Badge bg="success" className="ms-2">New</Badge>
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="buyer">
                  <FontAwesomeIcon icon={faHome} className="me-2" />
                  Buyer Profile
                  {!clientDetails.buyerProfile && (
                    <Badge bg="secondary" className="ms-2">Not Created</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="seller">
                  <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                  Seller Profile
                  {!clientDetails.sellerProfile && (
                    <Badge bg="secondary" className="ms-2">Not Created</Badge>
                  )}
                </Nav.Link>
              </Nav.Item>
              {clientDetails.sellerProfile && (
                <Nav.Item>
                  <Nav.Link eventKey="seller-ai">
                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                    Seller AI
                    <Badge bg="primary" className="ms-2">Comp Analysis</Badge>
                  </Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey="activity" disabled>
                  <FontAwesomeIcon icon={faClock} className="me-2" />
                  Activity
                  <Badge bg="secondary" className="ms-2">Coming Soon</Badge>
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body className="p-4">
            <Tab.Content>
              {/* Overview Tab */}
              <Tab.Pane eventKey="overview">
                <Row>
                  <Col md={6}>
                    <Card className="border-0 bg-light mb-4">
                      <Card.Body>
                        <h6 className="text-uppercase text-muted mb-3">AI Analysis</h6>
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span>Profile Completeness</span>
                            <strong>{clientDetails.aiContext.completenessScore}%</strong>
                          </div>
                          <div className="progress" style={{ height: '10px' }}>
                            <div 
                              className={`progress-bar ${
                                clientDetails.aiContext.completenessScore > 80 ? 'bg-success' :
                                clientDetails.aiContext.completenessScore > 50 ? 'bg-warning' : 'bg-danger'
                              }`}
                              style={{ width: `${clientDetails.aiContext.completenessScore}%` }}
                            />
                          </div>
                        </div>
                        {clientDetails.aiContext.completenessScore < 60 && (
                          <Alert variant="info" className="py-2 px-3 small">
                            <FontAwesomeIcon icon={faLightbulb} className="me-2" />
                            Complete the profile to unlock better AI search results
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 bg-light mb-4">
                      <Card.Body>
                        <h6 className="text-uppercase text-muted mb-3">Portal Access</h6>
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <FontAwesomeIcon 
                              icon={clientDetails.hasPortalAccess ? faCheckCircle : faUserPlus} 
                              className={`me-2 ${clientDetails.hasPortalAccess ? 'text-success' : 'text-muted'}`}
                            />
                            {clientDetails.hasPortalAccess ? 'Portal Access Active' : 'Portal Access Not Enabled'}
                          </div>
                          {!clientDetails.hasPortalAccess && (
                            <Button size="sm" variant="primary" onClick={enablePortalAccess}>
                              Enable Now
                            </Button>
                          )}
                        </div>
                        {clientDetails.portalLink && (
                          <div className="mt-3 p-2 bg-white rounded">
                            <small className="text-muted d-block">Portal Link:</small>
                            <code className="small">{clientDetails.portalLink}</code>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {!clientDetails.buyerProfile && !clientDetails.sellerProfile && (
                  <Alert variant="info" className="text-center py-5">
                    <FontAwesomeIcon icon={faClipboardList} size="3x" className="mb-3 text-info" />
                    <h5>No Profile Created Yet</h5>
                    <p className="mb-0">Create a buyer or seller profile to start using AI-powered features</p>
                  </Alert>
                )}

                {/* AI Property Recommendations Section */}
                {clientDetails.buyerProfile && (
                  <div className="ai-property-section mt-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h5 className="mb-0 d-flex align-items-center">
                        <FontAwesomeIcon icon={faWandMagicSparkles} className="text-primary me-2" />
                        AI-Powered Property Matches
                      </h5>
                      {!aiPropertiesLoading && aiProperties.length > 0 && (
                        <Button 
                          size="sm" 
                          variant="outline-primary"
                          onClick={fetchAiPropertyMatches}
                        >
                          <FontAwesomeIcon icon={faRefresh} className="me-1" />
                          Refresh
                        </Button>
                      )}
                    </div>

                    {aiPropertiesLoading && (
                      <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" className="mb-3" />
                        <p className="text-muted">
                          <FontAwesomeIcon icon={faBrain} className="me-2 pulse" />
                          AI is analyzing properties based on buyer preferences...
                        </p>
                      </div>
                    )}

                    {!aiPropertiesLoading && aiError && (
                      <Alert variant="danger" className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {aiError}
                      </Alert>
                    )}

                    {!aiPropertiesLoading && !aiError && aiProperties.length === 0 && (
                      <Alert variant="info" className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faHome} className="me-2" />
                        No properties found matching the buyer's preferences. Try adjusting search criteria.
                      </Alert>
                    )}

                    {!aiPropertiesLoading && !aiError && aiProperties.length > 0 && (
                      <>
                        {aiSummary && (
                          <Alert variant="success" className="mb-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <FontAwesomeIcon icon={faMagic} className="me-2" />
                                Found <strong>{aiSummary.totalMatches}</strong> properties
                                {aiSummary.perfectMatches > 0 && (
                                  <span> including <strong>{aiSummary.perfectMatches}</strong> perfect matches!</span>
                                )}
                              </div>
                              <small className="text-muted">Powered by Mistral 7B</small>
                            </div>
                          </Alert>
                        )}

                        <Row>
                          {aiProperties.map((property, index) => (
                            <Col lg={6} className="mb-4" key={property.id || index}>
                              <div className="position-relative">
                                {/* Match Score Badge */}
                                {property.matchScore && (
                                  <div 
                                    className={`position-absolute top-0 end-0 m-2 z-index-10`}
                                    style={{ zIndex: 10 }}
                                  >
                                    <Badge 
                                      bg={property.matchScore >= 90 ? 'success' : property.matchScore >= 70 ? 'primary' : 'warning'}
                                      className="px-3 py-2 fs-6"
                                    >
                                      {property.isTopMatch && <FontAwesomeIcon icon={faTrophy} className="me-1" />}
                                      {property.matchScore}% Match
                                    </Badge>
                                  </div>
                                )}

                                <PropertyCard 
                                  property={{
                                    id: property.id,
                                    mls_number: property.mls_number,
                                    price: property.price || property.list_price,
                                    address: property.address || property.street_address,
                                    city: property.city,
                                    state: property.state,
                                    bedrooms: property.bedrooms,
                                    bathrooms: property.bathrooms,
                                    square_feet: property.square_feet,
                                    images: property.images || [],
                                    matchScore: property.matchScore,
                                    relevanceScore: property.relevanceScore,
                                    isTopMatch: property.isTopMatch
                                  }}
                                />

                                {/* AI Reasoning Card */}
                                {property.aiReasoning && (
                                  <Card className="border-0 mt-2 shadow-sm" style={{ backgroundColor: '#f8f9ff' }}>
                                    <Card.Body className="py-3 px-3">
                                      <div className="d-flex align-items-start">
                                        <FontAwesomeIcon 
                                          icon={faLightbulb} 
                                          className="text-primary mt-1 me-2" 
                                        />
                                        <div className="flex-grow-1">
                                          <p className="mb-2 fw-bold text-primary">
                                            {property.aiReasoning.primary}
                                          </p>
                                          <div className="d-flex flex-wrap gap-2">
                                            {property.aiReasoning.factors.map((factor: string, idx: number) => (
                                              <Badge 
                                                key={idx} 
                                                bg="light" 
                                                text="dark"
                                                className="px-2 py-1"
                                              >
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-1" size="sm" />
                                                {factor}
                                              </Badge>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                )}
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </>
                    )}
                  </div>
                )}
              </Tab.Pane>

              {/* Buyer Profile Tab */}
              <Tab.Pane eventKey="buyer">
                {clientDetails.lead ? (
                  <LeadAiAssistant 
                    leadDetails={{
                      // Core lead details
                      id: clientDetails.lead.id,
                      lead_first_name: clientDetails.lead.name.split(' ')[0] || '',
                      lead_last_name: clientDetails.lead.name.split(' ').slice(1).join(' ') || '',
                      lead_email: clientDetails.lead.email,
                      lead_phone: clientDetails.lead.phone,
                      lead_source: clientDetails.lead.leadSource,
                      
                      // Lead type information
                      lead_type: clientDetails.lead.leadType || 'Buyer',
                      lead_types_id: clientDetails.profileType === 'buyer' ? 1 : 
                                     clientDetails.profileType === 'seller' ? 2 : 1,
                      
                      // Profile data
                      buyer_profiles: clientDetails.buyerProfile ? [clientDetails.buyerProfile] : [],
                      
                      // Additional fields the component may expect
                      created_on: clientDetails.lead.created,
                      updated_on: clientDetails.lead.created,
                      
                      // AI context
                      ai_context: clientDetails.aiContext
                    }}
                  />
                ) : (
                  <Alert variant="info" className="text-center py-5">
                    <FontAwesomeIcon icon={faHome} size="3x" className="mb-3 text-info" />
                    <h5>No Buyer Profile Created</h5>
                    <p className="mb-3">Create a buyer profile to track property preferences and search criteria</p>
                    <Button 
                      variant="primary" 
                      onClick={() => setShowBuyerProfileModal(true)}
                    >
                      <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                      Create Buyer Profile
                    </Button>
                  </Alert>
                )}
              </Tab.Pane>

              {/* Seller Profile Tab */}
              <Tab.Pane eventKey="seller">
                {clientDetails.sellerProfile ? (
                  <SellerProfilePage 
                    leadId={clientDetails.lead.id}
                    leadDetails={{
                      lead_first_name: clientDetails.lead.name.split(' ')[0] || '',
                      lead_last_name: clientDetails.lead.name.split(' ').slice(1).join(' ') || '',
                      lead_email: clientDetails.lead.email,
                      lead_phone: clientDetails.lead.phone,
                      id: clientDetails.lead.id
                    }}
                  />
                ) : (
                  <Alert variant="warning" className="text-center py-5">
                    <FontAwesomeIcon icon={faDollarSign} size="3x" className="mb-3 text-warning" />
                    <h5>No Seller Profile Created</h5>
                    <p className="mb-3">Create a seller profile to track property details and selling preferences</p>
                    <Button 
                      variant="warning" 
                      onClick={() => setShowSellerProfileModal(true)}
                    >
                      <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                      Create Seller Profile
                    </Button>
                  </Alert>
                )}
              </Tab.Pane>

              {/* AI Search Tab */}
              <Tab.Pane eventKey="ai-search">
                <AISearchTab 
                  leadId={leadId!} 
                  clientName={clientDetails.lead.name}
                  buyerProfile={clientDetails.buyerProfile}
                />
              </Tab.Pane>

              {/* Seller AI Tab - Comp Analysis */}
              {clientDetails.sellerProfile && (
                <Tab.Pane eventKey="seller-ai">
                  <SellerAICompAnalysis 
                    leadId={clientDetails.lead.id}
                    sellerProfile={clientDetails.sellerProfile}
                    propertyAddress={
                      clientDetails.sellerProfile.property_address ? 
                        `${clientDetails.sellerProfile.property_address}, ${clientDetails.sellerProfile.property_city}, ${clientDetails.sellerProfile.property_state} ${clientDetails.sellerProfile.property_zip}` : 
                        null
                    }
                    onEditProfile={() => setShowSellerProfileModal(true)}
                  />
                </Tab.Pane>
              )}
              
              {/* Activity Tab */}
              <Tab.Pane eventKey="activity">
                <Alert variant="info" className="text-center py-5">
                  <FontAwesomeIcon icon={faClock} size="3x" className="mb-3" />
                  <h5>Activity Timeline Coming Soon</h5>
                  <p className="mb-0">Track all client interactions, searches, and property views in one place</p>
                </Alert>
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>

      {/* Buyer Profile Modal */}
      {clientDetails && (
        <BuyerProfileModal
          show={showBuyerProfileModal}
          onHide={() => setShowBuyerProfileModal(false)}
          leadId={clientDetails.lead.id}
          leadName={clientDetails.lead.name}
          existingProfile={clientDetails.buyerProfile}
          onSave={handleProfileSave}
        />
      )}

      {/* Seller Profile Modal */}
      {clientDetails && (
        <SellerProfileModal
          show={showSellerProfileModal}
          onHide={() => setShowSellerProfileModal(false)}
          leadId={clientDetails.lead.id}
          leadName={clientDetails.lead.name}
          existingProfile={clientDetails.sellerProfile}
          onSave={handleProfileSave}
        />
      )}
    </div>
  );
};

export default ClientDetailsPage;